/*
 * @Author: shiguang
 * @Date: 2023-04-25 10:46:45
 * @LastEditors: yusha
 * @LastEditTime: 2024-06-27 10:31:20
 * @Description: App
 */
import React, {
	Suspense,
	createContext,
	lazy,
	useEffect,
	useState
} from 'react';
import { ConfigProvider, FloatingBubble } from 'antd-mobile';
import {
	NavigationType,
	RouterProvider,
	RouterProviderProps,
	createBrowserRouter,
	Location,
	useNavigate
} from 'react-router-dom';
import jaJp from 'antd-mobile/es/locales/ja-JP';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMount } from 'ahooks';
import routers from './config/router/index';
import { Lang, LocalContext } from './i18n/init';
import KeepAlive from './component/KeepAlive';
import BottomnavigationHomepageDefault from './common/icons/BottomnavigationHomepageDefault';
import { Local } from './i18n/i18n';

import '@/styles/theme/index.scss';
import '@/styles/theme/b2b-css-variable.scss';
import '@/styles/theme/d2c-css-variable.scss';
import {
	PlatLocalstorage,
	SiteLocalKey,
	setCookies,
	togglePlat
} from './config/request/login';
import { ENUM_SYSTEM_SOURCE } from './common/Enums';
import { systemUserSource } from './atoms/User';
import { atomUserDetail } from './atoms/userDetail';

interface RouterHistoryItem extends Location {
	navigationType: NavigationType;
}
export const RouterHistoryContext = createContext<RouterHistoryItem[]>([]);

interface PageContentProps {
	children?: React.ReactNode;
}

const isLocal = window.location.hostname === 'localhost';

const PageContent = (props: PageContentProps) => {
	const { children } = props;
	const navigate = useNavigate();

	const userDetail = useAtomValue(atomUserDetail);

	useMount(() => {
		const langJson = localStorage.getItem(SiteLocalKey);
		const lang = langJson ? JSON.parse(langJson)?.val ?? 'ja' : '';
		// ['ja', 'ko', 'en']
		// 需要监听语言，是否为日语
		// 如果已经登录
		if (
			userDetail?.customerId &&
			userDetail.stationCode !== 'JapanStation'
		) {
			// 如果登录的站点不是日本站
			window.location.href = '/';
		}
		// 如果未登录
		if (!userDetail?.customerId) {
			localStorage.setItem('__lang__', Lang.ja_JP);
			localStorage.setItem(
				SiteLocalKey,
				JSON.stringify({
					val: 'ja'
				})
			);
		}
	});

	return (
		<KeepAlive>
			<Suspense fallback={<div />}>
				<>
					{children}
					{isLocal && (
						<FloatingBubble
							axis="xy"
							magnetic="x"
							style={{
								'--initial-position-top': '2rem',
								'--initial-position-right': '24px',
								'--edge-distance': '24px'
							}}
							onClick={() => {
								navigate('/promotion/home', {
									replace: true
								});
							}}
						>
							<BottomnavigationHomepageDefault fontSize={22} />
						</FloatingBubble>
					)}
				</>
			</Suspense>
		</KeepAlive>
	);
};

const createRouter = (): RouterProviderProps['router'] => {
	const newrouters = [...routers];
	if (process.env.REACT_APP_ENV !== 'prod') {
		newrouters.unshift({
			path: '/dev/login',
			title: 'dev-login',
			component: lazy(() => import('./container/dev/Login'))
		});
	}
	return createBrowserRouter(
		newrouters.map((item) => {
			const Comp = item.component;
			return {
				element: <PageContent>{Comp && <Comp />}</PageContent>,
				path: item.path,
				errorElement: <div>err</div>
			};
		}),
		{
			basename: '/h5'
		}
	);
};
export const router = createRouter();

function App() {
	const setSystemUserSource = useSetAtom(systemUserSource);
	const userDetail = useAtomValue(atomUserDetail);
	const [routerHistoryContextValue, setRouterHistoryContextValue] = useState<
		RouterHistoryItem[]
	>([]);
	router.subscribe((routerData) => {
		setRouterHistoryContextValue((preState) => {
			const [preRouterData] = preState;
			const isEqual = routerData.location.key === preRouterData?.key;
			if (isEqual) return preState;
			return [
				{
					...routerData.location,
					navigationType: routerData.historyAction
				},
				...preState
			];
		});
	});
	useEffect(() => {
		console.log(111111111, process.env.REACT_APP_ENV);
		// 默认取内存里面的字段
		const plat = localStorage.getItem(PlatLocalstorage);
		let platValue = !plat ? 'd2c' : JSON.parse(plat).val;
		let systemSource =
			platValue === 'd2c'
				? ENUM_SYSTEM_SOURCE.D2C
				: ENUM_SYSTEM_SOURCE.B2B;
		// 如果已经登录,直接取用户信息里面返回的平台
		if (userDetail?.systemSource) {
			systemSource = userDetail?.systemSource;
			platValue = systemSource === 1 ? 'd2c' : 'b2b';
			localStorage.setItem('systemSource', String(systemSource));
		}
		const curentSystemSource = systemSource ?? ENUM_SYSTEM_SOURCE.D2C;
		// 更换平台、切换肤色
		togglePlat(curentSystemSource);
		// 设置atom里。用来切换banner的数据
		setSystemUserSource(curentSystemSource);
		window.document.documentElement.setAttribute(
			'data-theme',
			platValue.toUpperCase() ?? 'D2C'
		);

		if (userDetail?.customerId) {
			setCookies(true);
		} else {
			setCookies(false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setSystemUserSource, userDetail]);

	return (
		<div id="J_B2B" className="App">
			<RouterHistoryContext.Provider value={routerHistoryContextValue}>
				<LocalContext.Provider value={Lang.ja_JP}>
					<ConfigProvider locale={jaJp}>
						<RouterProvider
							router={router}
							fallbackElement={<div>fallbackElement</div>}
						/>
					</ConfigProvider>
				</LocalContext.Provider>
			</RouterHistoryContext.Provider>
		</div>
	);
}

export default App;
